import React, { useState } from 'react';
import { Row, Col } from 'reactstrap';
import { toast } from 'react-toastify';
import MemberMembershipService from '../../../../../../services/member_membership_service';
import { NeoButton, NeoInput } from '../../../../../../components/Common';

/**
 * ActionAdjustCredits
 * Permite ao gestor adicionar ou remover créditos de aulas do pacote.
 * Útil para cortesias, correções ou ajustes manuais.
 */
const ActionAdjustCredits = ({ membership, idTenant, user, onRefresh }) => {
    const [adjustAmount, setAdjustAmount] = useState("");
    const [adjustType, setAdjustType] = useState("add"); // "add" | "remove"
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);

    const currentRemaining = membership.remainingClasses || 0;
    const currentTotal = membership.totalClasses || 0;

    const handleApply = async () => {
        const n = parseInt(adjustAmount, 10);
        if (!n || n <= 0) return toast.warning("Informe uma quantidade válida.");

        if (adjustType === "remove" && n > currentRemaining) {
            return toast.error(`Não é possível remover ${n} aulas. Saldo atual: ${currentRemaining}.`);
        }

        setLoading(true);
        try {
            const delta = adjustType === "add" ? n : -n;
            const newRemaining = currentRemaining + delta;
            const newTotal = adjustType === "add" ? currentTotal + delta : currentTotal;

            const updateFields = {
                remainingClasses: newRemaining,
                totalClasses: newTotal,
                updatedReason: reason || (adjustType === "add" ? "Adição manual de aulas" : "Remoção manual de aulas")
            };

            // Se estava expirado por falta de créditos e estamos adicionando, reativar
            if (adjustType === "add" && membership.status === "expired" && newRemaining > 0) {
                updateFields.status = "active";
            }

            await MemberMembershipService.update(membership.id, idTenant, user, updateFields);
            toast.success(`Ajuste concluído: ${n} aula(s) ${adjustType === "add" ? "adicionada(s)" : "removida(s)"}.`);
            setAdjustAmount("");
            setReason("");
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error("Erro ao ajustar créditos.");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const previewRemaining = adjustAmount && !isNaN(adjustAmount) && adjustAmount > 0
        ? currentRemaining + (adjustType === "add" ? +adjustAmount : -adjustAmount)
        : null;

    return (
        <div className="p-4 border mt-3 bg-white shadow-sm" style={{ borderRadius: '12px', borderColor: 'rgba(0, 0, 0, 0.06)', fontFamily: 'Outfit, sans-serif' }}>
            <div className="d-flex align-items-center mb-3">
                <i className="mdi mdi-ticket-percent-outline fs-4 me-2 text-muted"></i>
                <h6 className="fw-bold mb-0 text-uppercase" style={{ letterSpacing: "0.5px", color: 'var(--color-brand-primary)', fontFamily: 'Outfit, sans-serif' }}>
                    Ajustar Créditos de Aulas
                </h6>
            </div>

            <p className="text-muted font-size-13 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Adicione aulas como cortesia ou compensação. Remova aulas para corrigir créditos lançados incorretamente.
            </p>

            <Row className="g-3 align-items-start" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Col md={2}>
                    <NeoInput
                        label="Operação"
                        type="select"
                        value={adjustType}
                        onChange={e => setAdjustType(e.target.value)}
                        size="sm"
                    >
                        <option value="add">Adicionar</option>
                        <option value="remove">Remover</option>
                    </NeoInput>
                </Col>
                <Col md={2}>
                    <NeoInput
                        label="Quantidade"
                        type="number"
                        min="1"
                        placeholder="Ex: 5"
                        value={adjustAmount}
                        onChange={e => setAdjustAmount(e.target.value)}
                        size="sm"
                    />
                </Col>
                <Col md={5}>
                    <NeoInput
                        label="Motivo (Auditoria)"
                        placeholder="Descreva a razão do ajuste..."
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        size="sm"
                    />
                </Col>
                <Col md={3} className="d-flex align-items-end" style={{ height: "62px" }}>
                    <NeoButton
                        variant="primary"
                        onClick={handleApply}
                        loading={loading}
                        disabled={!adjustAmount || adjustAmount <= 0}
                        size="sm"
                        className="w-100 mb-0"
                        style={{ height: "36px" }}
                    >
                        Confirmar
                    </NeoButton>
                </Col>
            </Row>

            {previewRemaining !== null && (
                <div className="mt-4 pt-3 border-top" style={{ borderColor: 'rgba(72, 39, 18, 0.08)' }}>
                    <div className="font-size-11 text-muted text-uppercase mb-2 fw-bold" style={{ letterSpacing: "0.5px" }}>
                        Impacto no Saldo de Aulas
                    </div>
                    <div className="d-flex align-items-center p-3 rounded-3 border bg-white shadow-sm" style={{ borderColor: 'rgba(72, 39, 18, 0.08)' }}>
                        <div className="flex-grow-1 text-center">
                            <div className="text-muted font-size-11 text-uppercase mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Saldo Atual</div>
                            <div className="fw-bold font-size-15 text-dark" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                <i className="mdi mdi-ticket-outline me-1"></i>
                                {currentRemaining}/{currentTotal}
                            </div>
                        </div>
                        <div className="px-3 text-muted">
                            <i className="mdi mdi-arrow-right fs-4"></i>
                        </div>
                        <div className="flex-grow-1 text-center">
                            <div className="text-muted font-size-11 text-uppercase mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Novo Saldo</div>
                            <div className={`fw-bold font-size-15 ${previewRemaining <= 0 ? 'text-danger' : 'text-success'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                                <i className="mdi mdi-ticket-outline me-1"></i>
                                {Math.max(0, previewRemaining)}/{adjustType === "add" ? currentTotal + (+adjustAmount) : currentTotal}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ActionAdjustCredits;
